import { Bitcoin, ChartLine, Coins, CreditCard, Gem, TrendingUp } from '@tamagui/lucide-icons';
import { Image } from '@unistocks/components/basic/image';
import { Card } from '@unistocks/components/layout/card';
import { Row } from '@unistocks/components/layout/flex';
import { UnistocksCircle } from '@unistocks/components/logo/unistocks-circle';
import { H4, Paragraph } from '@unistocks/components/text/text';
import { AssetType } from '@unistocks/proxy/axios-client';
import { CASH, SAVINGS_ACCOUNT } from '@unistocks/shared/consts';
import { t } from '@unistocks/shared/localization/index.ts';
import { BITCOIN } from '@unistocks/shared/styles/colors';
import { translateEnumOptions } from '@unistocks/shared/utils/enum-utils';
import { assetTypeOptions } from '@unistocks/shared/utils/enums/asset-type-options';
import { useMemo } from 'react';
import { useMedia } from 'tamagui';

export type AssetTypeSelectProps = {
  assetTypes?: AssetType[];
  assetType?: AssetType;
  onChange: (type: AssetType) => void;
};

const assetTypeOrder = [
  AssetType.Stock,
  AssetType.Fund,
  AssetType.Index,
  AssetType.CryptoCurrency,
  AssetType.Commodity,
  AssetType.Other,
  AssetType.Cash,
  AssetType.SavingsAccount,
  AssetType.Loan
];

export const AssetTypeSelect: React.FC<AssetTypeSelectProps> = ({
  assetTypes,
  assetType,
  onChange
}) => {
  const media = useMedia();

  const translatedOptions = useMemo(() => {
    let options = assetTypeOptions;

    if (assetTypes) {
      options = options.filter(option => assetTypes.includes(option.value));
    }

    return translateEnumOptions(options).sort(
      (a, b) => assetTypeOrder.indexOf(a.value) - assetTypeOrder.indexOf(b.value)
    );
  }, [assetTypes]);

  return (
    <Row
      gap={'$3'}
      mb={'$3'}
      fd={media.md ? 'row' : 'column'}
      flexWrap="wrap"
      justifyContent="center"
    >
      {translatedOptions.map(({ key, value: type }) => (
        <Card
          key={type}
          bordered
          pressable
          hoverable
          width={media.md ? '48%' : '100%'}
          bg={type === assetType ? '$color4' : 'transparent'}
          onPress={() => onChange(type)}
        >
          <Card.Header>
            <H4>{key}</H4>
            {renderItemIcon(type)}
          </Card.Header>
          <Card.Body>
            <Paragraph>{t(`::Enum:AssetType:${AssetType[type]}:Description`)}</Paragraph>
          </Card.Body>
        </Card>
      ))}
    </Row>
  );
};

const renderItemIcon = (type: AssetType) => {
  switch (type) {
    case AssetType.Stock:
      return <ChartLine color={'$unistocks'} />;

    case AssetType.Fund:
      return <Coins color={'$headingFont'} />;

    case AssetType.Index:
      return <TrendingUp color={'$headingFont'} />;

    case AssetType.CryptoCurrency:
      return <Bitcoin color={BITCOIN} />;

    case AssetType.Cash:
      return <Image source={CASH} />;

    case AssetType.SavingsAccount:
      return <Image source={SAVINGS_ACCOUNT} />;

    case AssetType.Loan:
      return <CreditCard color={'$headingFont'} />;

    case AssetType.Commodity:
      return <Gem color={'$headingFont'} />;

    case AssetType.Other:
      return <UnistocksCircle />;
  }
};
